import type { TOC } from '@ember/component/template-only';
import { gt } from 'tio-ui/utilities';
import { PhCheck } from 'ember-phosphor-icons';
import { t } from 'ember-intl';

export interface WizardStep {
  number: number;
  label: string;
  completed: boolean;
  active: boolean;
}

interface WizardStepIndicatorSignature {
  Args: {
    /** Array of step definitions with number, label, completed, and active states */
    steps: WizardStep[];
  };
  Element: HTMLDivElement;
}

/**
 * WizardStepIndicator - Horizontal step progression for multi-step wizard
 *
 * Displays a horizontal bar of steps with completed/active/pending states.
 * Uses indigo for active, emerald for completed, gray for pending.
 *
 * @example Basic usage
 * ```gts
 * <WizardStepIndicator @steps={{this.steps}} />
 * ```
 */
const WizardStepIndicator: TOC<WizardStepIndicatorSignature> = <template>
  <div
    class="tio-row-flush gap-0 overflow-x-auto rounded-xl border tio-border-default tio-bg-surface p-3"
    role="navigation"
    aria-label={{t "tuition_assistance.reimagined.wizard.progress_label"}}
    ...attributes
  >
    {{#each @steps as |step index|}}
      {{#if (gt index 0)}}
        <div
          class="tio-tas-wizard-connector
            {{if
              step.completed
              'tio-tas-wizard-connector--completed'
              'tio-tas-wizard-connector--pending'
            }}"
        />
      {{/if}}
      <div class="tio-row-tight shrink-0 px-2 py-1">
        <div
          class="tio-tas-wizard-step
            {{if
              step.completed
              'tio-tas-wizard-step--completed'
              (if step.active 'tio-tas-wizard-step--active' 'tio-tas-wizard-step--pending')
            }}"
        >
          {{#if step.completed}}
            <PhCheck @size={{14}} @weight="bold" />
          {{else}}
            {{step.number}}
          {{/if}}
        </div>
        <span
          class="tio-text-sm-medium whitespace-nowrap
            {{if
              step.completed
              'tio-fg-success-body'
              (if step.active 'tio-fg-info-body' 'tio-fg-disabled')
            }}"
        >
          {{step.label}}
        </span>
      </div>
    {{/each}}
  </div>
</template>;

export default WizardStepIndicator;
